import { inject, Injectable, Renderer2 } from '@angular/core';
import { ScriptService } from '@zero/shared/util-services';
import { environment } from 'environments/environment';
import { GooglePlacesResult } from '../../models/google-places-results.model';

@Injectable({
  providedIn: 'root',
})
export class GooglePlacesService {
  readonly #scriptService = inject(ScriptService);

  public init(renderer: Renderer2): Promise<HTMLScriptElement> {
    return this.#scriptService.injectScript(
      `https://maps.googleapis.com/maps/api/js?libraries=places&key=${environment.googleMapsKey}`,
      renderer,
      10000,
    );
  }

  public getLocation(latitude: number, longitude: number) {
    return this.#getFirstResult({ location: { lat: latitude, lng: longitude } });
  }

  public validateAddress(address: string) {
    return this.#getFirstResult({ address });
  }

  public getResultsByQuery(query: string) {
    return this.#getResults({ address: query });
  }

  public addressAsString(location: GooglePlacesResult) {
    return `${location.street} ${location.streetNumber} ${location.zip} ${location.city} ${location.country}`;
  }

  #getFirstResult(request: google.maps.GeocoderRequest): Promise<GooglePlacesResult> {
    return new Promise<GooglePlacesResult>((resolve, reject) => {
      new google.maps.Geocoder().geocode(request, (results, status) => {
        if (status == google.maps.GeocoderStatus.OK && results[0]) {
          resolve(this.#transformToGooglePlacesResult(results[0].address_components));
        } else {
          reject('No address found');
        }
      });
    });
  }

  #getResults(request: google.maps.GeocoderRequest): Promise<GooglePlacesResult[]> {
    return new Promise<GooglePlacesResult[]>((resolve, reject) => {
      new google.maps.Geocoder().geocode(request, (results, status) => {
        if (status == google.maps.GeocoderStatus.OK && results?.length > 0) {
          resolve(results.map((result) => this.#transformToGooglePlacesResult(result.address_components)));
        } else {
          reject(new Error('No addresses found'));
        }
      });
    });
  }

  #transformToGooglePlacesResult(components: google.maps.GeocoderAddressComponent[]) {
    return {
      streetNumber: this.#getComponentByType(components, 'street_number')?.long_name,
      street: this.#getComponentByType(components, 'route')?.long_name,
      city: this.#getComponentByType(components, 'locality')?.long_name,
      zip: this.#getComponentByType(components, 'postal_code')?.long_name,
      country: this.#getComponentByType(components, 'country')?.short_name,
    };
  }

  #getComponentByType(
    components: google.maps.GeocoderAddressComponent[],
    type: 'street_number' | 'route' | 'locality' | 'postal_code' | 'country',
  ) {
    return components.find((component) => component.types.includes(type));
  }
}
